<template>
  <div class="room">
    <!-- Видео контейнер -->
    <VideoContainer
      :local-stream="localStream"
      :remote-stream="remoteStream"
      :display-name="displayName"
      :remote-peer-name="remotePeerName"
      :remote-video-muted="remoteVideoMuted"
      :remote-audio-muted="remoteAudioMuted"
      :has-video="hasVideo"
      :video-muted="videoMuted"
      :is-reconnecting="isReconnecting"
      @toggle-controls="toggleControls"
      @mouse-move="showControlsOnMouseMove"
      @mouse-leave="hideControlsOnMouseLeave"
      @touch-start="showControlsOnTouch"
      @touch-end="hideControlsOnTouchEnd"
    />

    <!-- Оверлей ввода имени / начала звонка -->
    <div v-if="!nameEntered || !playbackUnlocked" class="start-call-overlay">
      <div class="start-call-content">
        <div class="start-icon">
          <i :class="nameEntered ? 'fas fa-phone' : 'fas fa-user'"></i>
        </div>

        <!-- Ввод имени -->
        <div v-if="!nameEntered">
          <h2>Введите ваше имя</h2>
          <p>Как к вам обращаться?</p>
          <input
            v-model="tempName"
            @keyup.enter="submitName"
            type="text"
            placeholder="Ваше имя"
            class="name-input"
            autofocus
          />
          <button @click="submitName" class="start-call-btn" :disabled="!tempName.trim()">
            <i class="fas fa-arrow-right"></i>
            Продолжить
          </button>
        </div>

        <!-- Начало звонка -->
        <div v-else>
          <h2>Готов начать звонок</h2>
          <p>Нажмите кнопку ниже для начала</p>
          <button @click="unlockPlayback" class="start-call-btn">
            <i class="fas fa-play"></i>
            Начать звонок
          </button>
        </div>
      </div>
    </div>

    <!-- Панель управления -->
    <ControlPanel
      :visible="controlsVisible"
      :audio-muted="audioMuted"
      :video-muted="videoMuted"
      :has-video="hasVideo"
      :can-switch-cameras="cameraInfo.canSwitchCameras"
      :camera-type="cameraInfo.currentCamera?.type"
      :show-device-menu="showDeviceMenu"
      :show-stats="showStats"
      @toggle-audio="toggleAudio"
      @toggle-video="toggleVideo"
      @switch-camera="handleCameraSwitch"
      @toggle-device-menu="toggleDeviceMenu"
      @toggle-stats="toggleStats"
      @copy-link="handleCopyLink"
      @leave-room="leaveRoom"
    />

    <!-- Панель статистики -->
    <StatsPanel
      :visible="showStats"
      :connection-state="connectionState"
      :connection-type="connectionType"
      :stats="stats"
      :current-quality="currentQuality"
      :peer-metrics="peerMetrics"
      :remote-peer-metrics="remotePeerMetrics"
      :remote-peer-name="remotePeerName"
      :is-reconnecting="isReconnecting"
      :camera-info="cameraInfo.currentCamera"
      @close="toggleStats"
    />

    <!-- Меню настроек устройств -->
    <DeviceMenu
      :visible="showDeviceMenu"
      :has-video="hasVideo"
      :current-quality="currentQuality"
      :quality-profiles="qualityProfiles"
      :device-capabilities="deviceCapabilities"
      :camera-info="cameraInfo"
      :audio-device-info="audioDeviceInfo"
      :is-quality-supported="isQualitySupported"
      @close="toggleDeviceMenu"
      @change-quality="changeQuality"
      @select-audio-input="selectAudioInput"
      @select-audio-output="selectAudioOutput"
      @select-camera="selectCamera"
    />

    <!-- Уведомления -->
    <div v-if="!!uiNotification" class="uiNotification" :class="uiNotification.type">
      {{ uiNotification.message }}
    </div>

    <!-- Модальное окно для запроса разрешений -->
    <div v-if="showPermissionError" class="permission-modal">
      <div class="permission-modal-content">
        <div class="permission-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h2>Необходим доступ к микрофону или камере</h2>
        <p class="permission-description">
          Для участия в видеозвонке требуется доступ к микрофону или камере.
        </p>
        <div class="permission-instructions">
          <h3>Как разрешить доступ:</h3>
          <ol>
            <li>Нажмите на иконку замка в адресной строке браузера</li>
            <li>Разрешите доступ к микрофону и камере</li>
            <li>Нажмите кнопку "Повторить попытку" ниже</li>
          </ol>
        </div>
        <div class="permission-actions">
          <button @click="retryMediaPermissions" class="btn-primary">
            <i class="fas fa-redo"></i>
            Повторить попытку
          </button>
          <button @click="router.push('/')" class="btn-secondary">
            <i class="fas fa-home"></i>
            На главную
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Компонент комнаты для видеозвонков
 * Использует модульные composables и компоненты
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useWebRTC } from '../composables/useWebRTC';
import { useSignaling } from '../composables/useSignaling';
import { useStorage } from '@vueuse/core';
import { isMobileDevice as checkMobileDevice } from '../utils/cameraDetection';
import { formatConnectionState } from '../utils/connectionState';

// Импорт компонентов
import VideoContainer from '../components/room/VideoContainer.vue';
import ControlPanel from '../components/room/ControlPanel.vue';
import StatsPanel from '../components/room/StatsPanel.vue';
import DeviceMenu from '../components/room/DeviceMenu.vue';

import type {
  QualityLevel,
  PeerMetrics,
  UiNotification
} from '../types';

// ==================== ИНИЦИАЛИЗАЦИЯ ====================

const route = useRoute();
const router = useRouter();
const roomId = route.params.roomId as string;

// ==================== СОСТОЯНИЕ ====================

/** Имя пользователя */
const displayName = ref<string>('');
/** Имя собеседника */
const remotePeerName = ref<string>('');
/** ID собеседника */
const remotePeerId = ref<string>('');
/** Являемся ли мы инициатором соединения */
const isInitiator = ref<boolean>(false);

/** Временное имя для ввода */
const tempName = ref<string>('');
/** Флаг ввода имени */
const nameEntered = ref<boolean>(false);

/** Perfect Negotiation: флаг создания offer */
const isMakingOffer = ref<boolean>(false);
/** Perfect Negotiation: флаг установки remote answer */
const isSettingRemoteAnswerPending = ref<boolean>(false);
/** Perfect Negotiation: флаг вежливого пира */
const isPolite = ref<boolean>(false);

/** Метрики собеседника */
const remotePeerMetrics = ref<PeerMetrics>({
  codecs: { video: null, audio: null },
  resolution: { width: 0, height: 0 },
  network: { rtt: 0, bitrateOut: 0, bitrateIn: 0, packetLoss: 0, connectionType: 'unknown' },
  media: { audioEnabled: true, videoEnabled: true },
  camera: { type: 'unknown', label: 'Unknown Camera', canSwitch: false, availableCameras: 0 },
  quality: '480p'
});

/** Флаг разблокировки воспроизведения */
const playbackUnlocked = ref<boolean>(false);

/** UI состояние */
const audioMuted = ref<boolean>(false);
const videoMuted = ref<boolean>(false);
const showStats = ref<boolean>(false);
const showDeviceMenu = ref<boolean>(false);
const controlsVisible = ref<boolean>(true);
const uiNotification = ref<UiNotification>();
const showPermissionError = ref<boolean>(false);

/** Таймеры для автоскрытия панели управления */
let hideControlsTimer: number | null = null;

/** Флаг для touch событий */
let touchHandled = ref(false);

/** Определение мобильного устройства */
const isMobileDevice = computed(() => checkMobileDevice());

// ==================== WEBRTC И СИГНАЛИНГ ====================

const {
  localStream,
  remoteStream,
  peerConnection,
  connectionState,
  connectionType,
  stats,
  currentQuality,
  isReconnecting,
  hasVideo,
  hasAudio,
  qualityProfiles,
  deviceCapabilities,
  cameraInfo,
  audioDeviceInfo,
  remoteVideoMuted,
  remoteAudioMuted,
  initWebRTC,
  createOffer,
  handleIceCandidate,
  setRemoteStream,
  toggleAudioTrack,
  toggleVideoTrack,
  reconnect: reconnectWebRTC,
  getPeerMetrics,
  isQualitySupported,
  applyQualitySafely,
  switchCamera,
  switchToSpecificCamera,
  switchAudioInput,
  switchAudioOutput,
  onLocalIceCandidate,
  onLocalOffer,
  onIceRestart,
  cleanup: cleanupWebRTC
} = useWebRTC();

const {
  connected: signalingConnected,
  connect: connectSignaling,
  join: joinRoom,
  sendReady,
  sendOffer,
  sendAnswer,
  sendIceCandidate,
  sendMetrics,
  leave: leaveSignaling,
  onJoined,
  onPeers,
  onPeerJoined,
  onPeerReconnected,
  onPeerReady,
  onOffer,
  onAnswer,
  onIceCandidate,
  onPeerLeft,
  onPeerMetrics
} = useSignaling();

/** Вычисляемое значение для peerMetrics */
const peerMetrics = computed(() => getPeerMetrics());

/** Текст состояния соединения */
const connectionStateText = computed(() => 
  formatConnectionState(connectionState.value, isReconnecting.value)
);

// ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ====================

/**
 * Безопасное декодирование из JWT
 */
function safeDecodeFromJWT(encodedText: string): string {
  if (!encodedText) return '';
  try {
    const binaryString = atob(encodedText);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch (error) {
    console.warn('Failed to decode text:', error);
    return encodedText;
  }
}

/**
 * Показывает уведомление
 */
function showNotification(message: string, type: UiNotification['type']): void {
  uiNotification.value = { message, type };
  setTimeout(() => {
    uiNotification.value = undefined;
  }, 3000);
}

// ==================== ИНИЦИАЛИЗАЦИЯ КОМНАТЫ ====================

/**
 * Общая функция инициализации комнаты с токеном
 */
async function initializeRoom(): Promise<void> {
  try {

    if (!token.value) {
      throw new Error('Токен не получен');
    }

    const initSuccess = await initWebRTC(token.value);

    if (!initSuccess) {
      console.error('❌ Не удалось инициализировать медиа');
      showPermissionError.value = true;
      showNotification('Необходим доступ к микрофону или камере', 'error');
      return;
    }

    if (!hasVideo.value && hasAudio.value) {
      showNotification('Камера не обнаружена. Работаем в режиме только аудио', 'warning');
    }

    setupWebRTCCallbacks();
    await connectSignaling();
    setupSignalingHandlers();
    await joinRoom(roomId, token.value, displayName.value);

    startMetricsSending();
    startAutoHideControls();

    console.log('✅ Инициализация комнаты завершена успешно');

  } catch (error) {
    console.error('❌ Не удалось инициализировать комнату:', error);
    showPermissionError.value = true;
    showNotification('Ошибка подключения: ' + (error as Error).message, 'error');
  }
}

/**
 * Устанавливает колбэки для WebRTC событий
 */
function setupWebRTCCallbacks(): void {
  onLocalIceCandidate((candidate) => {
    if (remotePeerId.value) {
      sendIceCandidate(candidate, remotePeerId.value);
    }
  });

  onLocalOffer((offer) => {
    if (remotePeerId.value) {
      sendOffer(offer.sdp || '', remotePeerId.value);
    }
  });

  onIceRestart((offer) => {
    if (remotePeerId.value) {
      sendOffer(offer.sdp || '', remotePeerId.value);
      showNotification('Переподключение...', 'info');
    }
  });
}

/**
 * Устанавливает обработчики сигналинга
 */
function setupSignalingHandlers(): void {
  // Успешно присоединились к комнате
  onJoined(async (data) => {
    console.log('✅ Joined room:', data);
    displayName.value = data.displayName;

    if (data.isReconnect) {
      console.log('🔄 This is a reconnect, restoring WebRTC...');
      isReconnecting.value = false;
      try {
        await reconnectWebRTC();
        showNotification('Переподключение восстановлено', 'info');
      } catch (error) {
        console.error('Failed to restore WebRTC on reconnect:', error);
        showNotification('Ошибка восстановления соединения', 'error');
      }
    }
  });

  // Список существующих участников
  onPeers((data) => {
    console.log('📋 Existing peers:', data);
    if (data.peers && data.peers.length > 0) {
      const peer = data.peers[0];
      remotePeerName.value = peer.displayName;
      remotePeerId.value = peer.peerId;

      const displayName = peer.displayName || 'Неизвестный пользователь';
      showNotification(`${displayName} уже в комнате`, 'info');

      isInitiator.value = false;
      isPolite.value = true;
      console.log('💡 Я второй участник (polite peer), жду offer');

      sendReady();
    }
  });

  // Новый peer присоединился
  onPeerJoined((data) => {
    console.log('👤 Peer joined:', data);
    remotePeerName.value = data.displayName;
    remotePeerId.value = data.peerId;
    showNotification(`${data.displayName} присоединился`, 'info');

    isInitiator.value = true;
    isPolite.value = false;
    console.log('💡 Я первый участник (impolite peer), жду ready от второго');
  });

  // Peer переподключился
  onPeerReconnected(async (data) => {
    console.log('🔄 Peer reconnected:', data);
    remotePeerName.value = data.displayName;
    remotePeerId.value = data.peerId;
    showNotification(`${data.displayName} вернулся`, 'info');

    isReconnecting.value = false;

    try {
      console.log('🔄 Reconnecting WebRTC...');
      await reconnectWebRTC();
      sendReady();
      console.log('✅ WebRTC reconnection initiated');
    } catch (error) {
      console.error('Failed to reconnect WebRTC:', error);
      showNotification('Ошибка переподключения', 'error');
    }
  });

  // Peer готов к соединению
  onPeerReady(async (data) => {
    console.log('📡 Peer ready:', data);

    if (isInitiator.value && !isMakingOffer.value) {
      console.log('💡 Создаю offer (я initiator)');
      isMakingOffer.value = true;
      try {
        const offer = await createOffer();
        sendOffer(offer.sdp || '', data.peerId);
      } catch (error) {
        console.error('Failed to create offer:', error);
        isMakingOffer.value = false;
      }
    } else {
      console.log('💡 Жду offer (я НЕ initiator)');
    }
  });

  // Получен offer
  onOffer(async (data) => {
    console.log('📨 Received offer from:', data.fromPeerId);

    try {
      // Perfect Negotiation: проверяем коллизии
      if (isMakingOffer.value || isSettingRemoteAnswerPending.value) {
        if (!isPolite.value) {
          console.log('⚠️ Ignoring offer (impolite peer, making offer)');
          return;
        } else {
          console.log('🔄 Rolling back our offer (polite peer)');
          await peerConnection.value!.setLocalDescription({ type: 'rollback' });
          isMakingOffer.value = false;
        }
      }

      isSettingRemoteAnswerPending.value = true;

      await peerConnection.value!.setRemoteDescription(new RTCSessionDescription({
        type: 'offer',
        sdp: data.sdp
      }));

      const answer = await peerConnection.value!.createAnswer();
      await peerConnection.value!.setLocalDescription(answer);

      sendAnswer(answer.sdp || '', data.fromPeerId);
      isSettingRemoteAnswerPending.value = false;

    } catch (error) {
      console.error('Failed to handle offer:', error);
      isSettingRemoteAnswerPending.value = false;
    }
  });

  // Получен answer
  onAnswer(async (data) => {
    console.log('📨 Received answer from:', data.fromPeerId);

    try {
      await peerConnection.value!.setRemoteDescription(new RTCSessionDescription({
        type: 'answer',
        sdp: data.sdp
      }));

      isMakingOffer.value = false;
      console.log('✅ Remote answer set');

    } catch (error) {
      console.error('Failed to set remote answer:', error);
    }
  });

  // Получен ICE candidate
  onIceCandidate((data) => {
    handleIceCandidate(data.candidate);
  });

  // Peer покинул комнату
  onPeerLeft((data) => {
    console.log('Peer left:', data);
    showNotification(`${remotePeerName.value || 'Собеседник'} покинул комнату`, 'warning');
    remotePeerName.value = '';
    setRemoteStream(null);
  });

  // Получены метрики от собеседника
  onPeerMetrics((data) => {
    console.log('📊 Received peer metrics:', data);
    const metrics = data.metrics?.metrics
    if (metrics) {
      remotePeerMetrics.value = {
        codecs: metrics.codecs || { video: null, audio: null },
        resolution: metrics.resolution || { width: 0, height: 0 },
        network: metrics.network || {
          rtt: 0,
          bitrateOut: 0,
          bitrateIn: 0,
          packetLoss: 0,
          connectionType: 'unknown'
        },
        media: metrics.media || { audioEnabled: true, videoEnabled: true },
        camera: metrics.camera || {
          type: 'unknown',
          label: 'Unknown Camera',
          canSwitch: false,
          availableCameras: 0
        },
        quality: metrics.quality || '480p'
      };
    }
  });
}

// ==================== УПРАВЛЕНИЕ МЕДИА ====================

/**
 * Переключает микрофон
 */
async function toggleAudio(): Promise<void> {
  audioMuted.value = !audioMuted.value;
  await toggleAudioTrack(!audioMuted.value);
}

/**
 * Переключает камеру (async для поддержки двойного контура)
 */
async function toggleVideo(): Promise<void> {
  videoMuted.value = !videoMuted.value;
  await toggleVideoTrack(!videoMuted.value);
}

/**
 * Обработка переключения камеры
 */
async function handleCameraSwitch(): Promise<void> {
  try {
    const success = await switchCamera();
    if (success) {
      const currentType = cameraInfo.value.currentCamera?.type;
      const typeText = currentType === 'front' ? 'фронтальную' : currentType === 'back' ? 'заднюю' : 'другую';
      showNotification(`Камера переключена на ${typeText}`, 'info');
    } else {
      showNotification('Не удалось переключить камеру', 'error');
    }
  } catch (error) {
    console.error('Failed to switch camera:', error);
    showNotification('Ошибка переключения камеры', 'error');
  }
}

// ==================== УПРАВЛЕНИЕ UI ====================

/**
 * Переключает панель статистики
 */
function toggleStats(): void {
  showStats.value = !showStats.value;
}

/**
 * Переключает меню устройств
 */
function toggleDeviceMenu(): void {
  showDeviceMenu.value = !showDeviceMenu.value;
}

/**
 * Копирует ссылку на текущую комнату в буфер обмена
 */
async function handleCopyLink(): Promise<void> {
  try {
    const currentUrl = window.location.href;
    await navigator.clipboard.writeText(currentUrl);
    showNotification('Ссылка скопирована в буфер обмена', 'success');
  } catch (error) {
    console.error('❌ Ошибка копирования ссылки:', error);
    showNotification('Не удалось скопировать ссылку', 'error');
  }
}

/**
 * Переключает видимость панели управления
 */
function toggleControls(): void {
  if (isMobileDevice.value || touchHandled.value) {
    return;
  }

  if (controlsVisible.value) {
    hideControls();
  } else {
    showControls();
  }
}

/**
 * Показывает панель управления
 */
function showControls(): void {
  controlsVisible.value = true;
  clearHideTimer();
  hideControlsTimer = window.setTimeout(() => {
    hideControls();
  }, 3000);
}

/**
 * Скрывает панель управления
 */
function hideControls(): void {
  controlsVisible.value = false;
  clearHideTimer();
}

/**
 * Показывает панель при движении мыши
 */
function showControlsOnMouseMove(): void {
  if (isMobileDevice.value) {
    return;
  }

  if (!controlsVisible.value) {
    showControls();
  } else {
    clearHideTimer();
    hideControlsTimer = window.setTimeout(() => {
      hideControls();
    }, 3000);
  }
}

/**
 * Скрывает панель при уходе мыши
 */
function hideControlsOnMouseLeave(): void {
  if (isMobileDevice.value) {
    return;
  }

  clearHideTimer();
  hideControlsTimer = window.setTimeout(() => {
    hideControls();
  }, 1000);
}

/**
 * Показывает панель при касании
 */
function showControlsOnTouch(): void {
  touchHandled.value = true;
  showControls();

  setTimeout(() => {
    touchHandled.value = false;
  }, 300);
}

/**
 * Скрывает панель после касания
 */
function hideControlsOnTouchEnd(): void {
  clearHideTimer();
  hideControlsTimer = window.setTimeout(() => {
    hideControls();
  }, 10000);
}

/**
 * Очищает таймер автоскрытия
 */
function clearHideTimer(): void {
  if (hideControlsTimer) {
    clearTimeout(hideControlsTimer);
    hideControlsTimer = null;
  }
}

/**
 * Запускает автоскрытие панели управления
 */
function startAutoHideControls(): void {
  hideControlsTimer = window.setTimeout(() => {
    hideControls();
  }, 15000);
}

// ==================== НАСТРОЙКИ УСТРОЙСТВ И КАЧЕСТВА ====================

/**
 * Изменяет качество видео
 */
async function changeQuality(quality: QualityLevel): Promise<void> {
  try {
    if (!isQualitySupported(quality)) {
      showNotification(`Качество ${quality} не поддерживается вашим устройством`, 'warning');
      return;
    }

    const success = await applyQualitySafely(quality);
    if (success) {
      showNotification(`Качество изменено на ${quality}`, 'info');
    } else {
      showNotification('Не удалось изменить качество', 'error');
    }
  } catch (error) {
    console.error('Failed to change quality:', error);
    showNotification('Ошибка изменения качества', 'error');
  }
}

/**
 * Выбирает микрофон
 */
async function selectAudioInput(deviceId: string): Promise<void> {
  try {
    const success = await switchAudioInput(deviceId);
    if (success) {
      const device = audioDeviceInfo.value.availableInputDevices.find(d => d.deviceId === deviceId);
      showNotification(`Микрофон изменен на: ${device?.label || 'Неизвестное устройство'}`, 'info');
    } else {
      showNotification('Не удалось изменить микрофон', 'error');
    }
  } catch (error) {
    console.error('Failed to select audio input:', error);
    showNotification('Ошибка изменения микрофона', 'error');
  }
}

/**
 * Выбирает динамики
 */
async function selectAudioOutput(deviceId: string): Promise<void> {
  try {
    const success = await switchAudioOutput(deviceId);
    if (success) {
      const device = audioDeviceInfo.value.availableOutputDevices.find(d => d.deviceId === deviceId);
      showNotification(`Динамики изменены на: ${device?.label || 'Неизвестное устройство'}`, 'info');
    } else {
      showNotification('Не удалось изменить динамики', 'error');
    }
  } catch (error) {
    console.error('Failed to select audio output:', error);
    showNotification('Ошибка изменения динамиков', 'error');
  }
}

/**
 * Выбирает камеру
 */
async function selectCamera(deviceId: string): Promise<void> {
  try {
    const camera = cameraInfo.value.availableCameras.find(c => c.deviceId === deviceId);
    if (!camera) {
      showNotification('Камера не найдена', 'error');
      return;
    }

    const success = await switchToSpecificCamera(camera);
    if (success) {
      showNotification(`Камера изменена на: ${camera.label}`, 'info');
    } else {
      showNotification('Не удалось изменить камеру', 'error');
    }
  } catch (error) {
    console.error('Failed to select camera:', error);
    showNotification('Ошибка изменения камеры', 'error');
  }
}

// ==================== ВЫХОД ИЗ КОМНАТЫ ====================

/**
 * Покидает комнату
 */
function leaveRoom(): void {
  if (confirm('Покинуть комнату?')) {
    router.push('/');
  }
}

// ==================== МЕТРИКИ ====================

let metricsInterval: number | null = null;

/**
 * Запускает автоматическую отправку метрик
 */
function startMetricsSending(): void {
  if (metricsInterval) return;

  metricsInterval = window.setInterval(() => {
    if (remotePeerId.value && signalingConnected.value) {
      const metrics = getPeerMetrics();
      sendMetrics({ to: remotePeerId.value, metrics });
      console.log('📊 Sent metrics to peer:', metrics);
    }
  }, 5000);
}

/**
 * Останавливает отправку метрик
 */
function stopMetricsSending(): void {
  if (metricsInterval) {
    window.clearInterval(metricsInterval);
    metricsInterval = null;
  }
}

// ==================== РАЗРЕШЕНИЯ ====================

/**
 * Повторная попытка получения разрешений
 */
async function retryMediaPermissions(): Promise<void> {
  console.log('🔄 Retrying media permissions...');
  showPermissionError.value = false;

  try {
    const initSuccess = await initWebRTC(token.value as string);

    if (!initSuccess) {
      showPermissionError.value = true;
      showNotification('Все еще нет доступа к микрофону или камере', 'error');
      return;
    }

    if (!hasVideo.value && hasAudio.value) {
      showNotification('Камера не обнаружена. Работаем в режиме только аудио', 'warning');
    } else {
      showNotification('Доступ к медиа устройствам получен!', 'success');
    }

    setupWebRTCCallbacks();
    startMetricsSending();

  } catch (error) {
    console.error('Failed to retry media permissions:', error);
    showPermissionError.value = true;
    showNotification('Ошибка получения доступа к медиа', 'error');
  }
}

// ==================== PLAYBACK UNLOCK ====================

/**
 * Разблокирует воспроизведение (для iOS и WebView)
 */
async function unlockPlayback(): Promise<void> {
  console.log('🔓 Unlocking playback...');

  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (AC && !(window as any).__ac) {
      (window as any).__ac = new AC();
    }

    playbackUnlocked.value = true;
    showControls();

    console.log('✅ Playback unlocked');

  } catch (error) {
    console.error('Failed to unlock playback:', error);
  }
}

// ==================== ВВОД ИМЕНИ ====================

/**
 * Обработчик ввода имени
 */
async function submitName(): Promise<void> {
  if (!tempName.value.trim()) {
    return;
  }

  const enteredName = tempName.value.trim();
  displayName.value = enteredName;
  nameEntered.value = true;

  if (!token.value) {
    try {
      const response = await fetch(`/api/rooms/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: enteredName })
      });

      if (!response.ok) {
        throw new Error('Не удалось войти в комнату');
      }

      const data: {token?: string} = await response.json();

      if (!data.token) {
        throw new Error('Токен не получен от сервера');
      }

      token.value = data.token;

      await initializeRoom();
    } catch (error) {
      console.error('Failed to get token:', error);
      showNotification('Ошибка подключения: ' + (error as Error).message, 'error');
    }
  }
}

// ==================== ЖИЗНЕННЫЙ ЦИКЛ ====================

/**
 * Инициализация при монтировании компонента
 */
const token = ref<string | null>(null);
onMounted(async () => {
  await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
    .then(stream => {
      console.log('Stream obtained:', stream);
    })
    .catch(error => {
      console.error('Error obtaining stream:', error);
    });
  try {
    token.value = localStorage.getItem(`token_${roomId}`);

    if (token.value) {
      try {
        const tokenPayload = JSON.parse(atob(token.value.split('.')[1]));
        displayName.value = safeDecodeFromJWT(tokenPayload.displayName || '');
        nameEntered.value = true;
        console.log('✅ Extracted displayName from token:', displayName.value);

        await initializeRoom();
        return;
      } catch (error) {
        console.error('Failed to parse token:', error);
        displayName.value = 'Guest';
      }
    }
  } catch (error) {
    console.error('Failed to initialize room:', error);
    showPermissionError.value = true;
    showNotification('Ошибка подключения: ' + (error as Error).message, 'error');
  }
});

/**
 * Очистка при размонтировании компонента
 */
onUnmounted(() => {
  stopMetricsSending();
  clearHideTimer();
  cleanupWebRTC();
  leaveSignaling();
});

/**
 * Наблюдатель за удаленным потоком (для обратной совместимости с VideoContainer)
 */
watch(remoteStream, (stream) => {
  // VideoContainer сам обрабатывает remoteStream через props
  console.log('Remote stream updated:', stream ? 'available' : 'null');
});

</script>

<style scoped>
.room {
  position: relative;
  width: 100vw;
  height: 100dvh;
  background: #000;
  overflow: hidden;
}

/* Оверлей "Начать звонок" для разблокировки автоплея */
.start-call-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
  animation: fadeIn 0.3s ease;
}

.start-call-content {
  text-align: center;
  padding: 2.5rem;
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.98), rgba(50, 50, 50, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.start-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s ease-in-out infinite;
}

.start-icon i {
  font-size: 36px;
  color: #fff;
}

@keyframes pulse {
  0%, 100% { opacity: 0.9; }
  50% { opacity: 1; }
}

.start-call-content h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 600;
}

.start-call-content p {
  margin: 0 0 1.5rem 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
}

.name-input {
  width: 100%;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #fff;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  text-align: center;
}

.name-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.name-input:focus {
  outline: none;
  border-color: rgba(59, 130, 246, 0.8);
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.start-call-btn {
  width: 100%;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.start-call-btn:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.start-call-btn:active {
  transform: translateY(0);
}

.start-call-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.start-call-btn:disabled:hover {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transform: none;
}

/* Уведомления */
.uiNotification {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 16px 24px;
  border-radius: 12px;
  font-weight: 500;
  z-index: 1000;
  animation: slideDown 0.3s ease;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translate(-50%, -20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.uiNotification.info {
  background: rgba(59, 130, 246, 0.9);
  color: #fff;
}

.uiNotification.warning {
  background: rgba(251, 191, 36, 0.9);
  color: #000;
}

.uiNotification.error {
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
}

.uiNotification.success {
  background: rgba(34, 197, 94, 0.9);
  color: #fff;
}

/* Модальное окно для запроса разрешений */
.permission-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.permission-modal-content {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.98), rgba(50, 50, 50, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 2.5rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
  text-align: center;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.permission-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: rgba(239, 68, 68, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.permission-icon i {
  font-size: 36px;
  color: #ef4444;
}

.permission-modal-content h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 600;
}

.permission-description {
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
}

.permission-instructions {
  text-align: left;
  background: rgba(0, 0, 0, 0.3);
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
}

.permission-instructions h3 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
}

.permission-instructions ol {
  margin: 0;
  padding-left: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
}

.permission-instructions li {
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.permission-instructions li:last-child {
  margin-bottom: 0;
}

.permission-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn-primary,
.btn-secondary {
  padding: 0.875rem 1.75rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .permission-modal-content {
    padding: 2rem 1.5rem;
    max-width: 95%;
  }

  .permission-modal-content h2 {
    font-size: 1.25rem;
  }

  .permission-instructions {
    padding: 1rem;
  }

  .permission-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>
